/**
 * Helpers for binding Yealink call-control to the correct headset.
 *
 * When more than one Yealink is connected, matching a HID interface on vendor id alone
 * binds call control to whichever Yealink enumerated first - often not the one the user
 * selected. These helpers pick the HID interface that belongs to the selected audio device,
 * matched on its USB product id.
 */

/** Yealink USB vendor id */
export const YEALINK_VENDOR_ID = 0x6993

/** Minimal shape of a HIDDevice needed to match Yealink interfaces (kept loose for testability) */
export interface YealinkHidLike {
	vendorId: number
	productId: number
	productName?: string
}

/**
 * Parse the numeric USB product id out of a MediaDevice.productId string like "6993:0410".
 * @param productId Product id string in "vvvv:pppp" hex form, or undefined
 * @returns The numeric product id, or undefined if it cannot be parsed
 */
export function parseUsbProductId(productId?: string): number | undefined {
	if (!productId) { return undefined }

	const match = /^([0-9a-fA-F]{4}):([0-9a-fA-F]{4})$/.exec(productId)
	if (!match) { return undefined }

	return parseInt(match[2]!, 16) // eslint-disable-line @typescript-eslint/no-non-null-assertion
}

/**
 * Pick the Yealink HID interface that belongs to the selected audio device.
 *
 * Yealink also exposes a "HID debug interface" that must be disregarded. When the selected
 * device's product id is known, an exact match is required and no arbitrary fallback is made
 * (so control is never bound to the wrong headset); the caller can then request access to the
 * specific device. When the product id is unknown, the first non-debug Yealink is returned as
 * a best effort.
 * @param devices HID devices currently granted to the page
 * @param wantedProductId USB product id of the selected audio device, or undefined if unknown
 */
export function findMatchingYealinkHid<T extends YealinkHidLike>(devices: T[], wantedProductId?: number): T | undefined {
	const yealinks = devices.filter(device => device.vendorId === YEALINK_VENDOR_ID && !device.productName?.toLowerCase().includes('debug'))
	if (yealinks.length === 0) { return undefined }

	if (wantedProductId != null) {
		return yealinks.find(device => device.productId === wantedProductId)
	}

	return yealinks[0]
}
